import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "react-axios";
import dayjs from "dayjs";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Skeleton from "react-loading-skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import TrackBox from "../../Components/Elements/TrackBox";
import TrackImage from "../../Components/Elements/Global/TrackImage";
import TrackFavorite from "../../Components/Icons/TrackFavorite";
import MediaSourceIcon from "../../Components/Icons/MediaSourceIcon";
import AddToPlaylistModal from "../../Components/Elements/AddToPlaylistModal";
import { AuthorName } from "../../Components/Elements/ChimneyName";
import { useUser } from "~/Context/userContext";
import { getTrack, getTrackReplies, getTracksPopularLastWeek } from "../../api/getTrack";
import "./PopularLastWeekPage.less";

const SHARE_PAGE_URL = "https://www.dreamchimney.com/tracks/popular/last-week";

const HIGHLIGHT_COUNT = 2;
const MIN_HIGHLIGHT_LENGTH = 24;

const stripHtml = (value) => {
  const text = value || "";
  if (typeof document === "undefined") {
    return text
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/\s+/g, " ")
      .trim();
  }
  const el = document.createElement("div");
  el.innerHTML = text;
  return (el.textContent || "").replace(/\s+/g, " ").trim();
};

const clipText = (text, max) => (text.length > max ? `${text.slice(0, max - 1).trim()}…` : text);

export const buildShareSummary = (tracks, weekRange) => {
  const lines = ["Popular tracks from last week on Dream Chimney"];
  if (weekRange) lines.push(weekRange);
  lines.push("");
  tracks.forEach((track) => {
    const likes = Number(track.likes) || 0;
    lines.push(`"${track.title}" — ${track.artist}`);
  });

  const quotes = [];
  tracks.forEach((track) => {
    pickHighlightComments(track.comments || []).forEach((comment) => {
      const text = stripHtml(comment.message);
      if (text.length >= MIN_HIGHLIGHT_LENGTH) {
        quotes.push({ text, name: comment.full_name });
      }
    });
  });
  quotes.sort((a, b) => b.text.length - a.text.length);


  lines.push("");
  lines.push(SHARE_PAGE_URL);
  return lines.join("\n");
};

export const pickHighlightComments = (comments) => {
  const usable = (comments || []).filter((comment) => stripHtml(comment.message).length > 0);
  const substantial = usable.filter((comment) => stripHtml(comment.message).length >= MIN_HIGHLIGHT_LENGTH);
  const pool = substantial.length ? substantial : usable;

  return pool
    .slice()
    .sort((a, b) => stripHtml(b.message).length - stripHtml(a.message).length)
    .slice(0, HIGHLIGHT_COUNT)
    .sort((a, b) => Number(a.id) - Number(b.id));
};

const formatWeekRange = (dates) => {
  if (!dates || dates.length < 2) return null;
  const start = dayjs(dates[0]);
  const end = dayjs(dates[1]);
  if (!start.isValid() || !end.isValid()) return null;
  if (start.year() === end.year()) {
    return `${start.format("MMMM D")} – ${end.format("MMMM D, YYYY")}`;
  }
  return `${start.format("MMMM D, YYYY")} – ${end.format("MMMM D, YYYY")}`;
};

const withFavCount = (track) => ({
  ...track,
  favs: track.likes != null ? track.likes : track.favs,
});

const TrackOverview = ({ tracks }) => (
  <nav className="popular-week-overview" aria-label="Popular tracks from last week">
    {tracks.map((track) => (
      <a key={track.id} href={`#popular-track-${track.id}`} className="popular-week-overview-item">
        <TrackImage track={track} link={false} width="100%" />
        <span className="popular-week-overview-title">&ldquo;{track.title}&rdquo;</span>
        <span className="popular-week-overview-artist">{track.artist}</span>
      </a>
    ))}
  </nav>
);

const isAdminRyan = (userState) => {
  const user = (userState && userState.user) || {};
  const chimDir = user.chim_dir || user.dir;
  return !!(userState.loggedin && userState.isChim && chimDir === "ryan");
};

const ShareSummary = ({ tracks, weekRange }) => {
  const { state: userState } = useUser();
  const isRyan = isAdminRyan(userState);
  const generated = buildShareSummary(tracks, weekRange);
  const [text, setText] = useState(generated);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setText(generated);
  }, [generated]);

  if (!isRyan) return null;

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <section className="popular-week-share">
      <h2>Share summary</h2>
      <p>Only you can see this. Edit it, then copy it for Twitter or another post.</p>
      <textarea value={text} onChange={(event) => setText(event.target.value)} rows={14} />
      <div className="popular-week-share-actions">
        <Button variant="outline-dark" size="sm" onClick={copyText}>
          {copied ? "Copied" : "Copy text"}
        </Button>
        <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer">
          Post on Twitter
        </a>
        <span>{text.length} characters</span>
      </div>
    </section>
  );
};

const PopularTrack = ({ track }) => {
  const comments = track.comments || [];
  const highlights = pickHighlightComments(comments);
  const hasMore = comments.length > highlights.length;

  return (
    <article className="popular-week-track" id={`popular-track-${track.id}`}>
      <div className="popular-week-track-main">
        <div className="popular-week-art">
          <TrackBox track={track} showTitle={false} showDate={false} />
        </div>
        <div className="popular-week-meta">
          <h2 className="popular-week-title">
            <Link to={`/${track.id}`}>&ldquo;{track.title}&rdquo;</Link>
          </h2>
          <div className="popular-week-artist">{track.artist}</div>
          <div className="popular-week-actions footer-left">
            <div className="footer-item footer-track-favorite">
              <TrackFavorite track={withFavCount(track)} />
            </div>
            <div className="footer-item">
              <AddToPlaylistModal trackId={track.id} />
            </div>
            {track.sc_url && (
              <div className="footer-item">
                <MediaSourceIcon track={track} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="popular-week-comments">
        <h3>
          <FontAwesomeIcon icon="comment" /> From the comments
        </h3>
        {highlights.length === 0 && <p className="popular-week-no-comments">No comments on this track yet.</p>}
        {highlights.map((comment) => (
          <blockquote className="popular-comment-highlight" key={comment.id}>
            <div className="popular-comment-text" dangerouslySetInnerHTML={{ __html: comment.message }} />
            <div className="popular-comment-by">
              <AuthorName user={comment} />
              {comment.date && <span className="popular-comment-date">{comment.date}</span>}
            </div>
          </blockquote>
        ))}
        {hasMore && (
          <div className="popular-week-all-comments">
            <Link to={`/${track.id}`}>Read all comments in the full thread</Link>
          </div>
        )}
        <div className="popular-week-add-comment">
          <Link to={`/${track.id}#trackComment`}>
            <FontAwesomeIcon icon="comment" /> Add a comment &raquo;
          </Link>
        </div>
      </div>
    </article>
  );
};

const PopularLastWeekPage = () => {
  const [tracks, setTracks] = useState([]);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await getTracksPopularLastWeek(axios);
        const list = (response.data && response.data.tracks) || [];
        const weekDates = (response.data && response.data.dates) || [];
        const withComments = await Promise.all(
          list.map(async (track) => {
            const [repliesResult, likes] = await Promise.all([
              getTrackReplies(axios, track.id).catch(() => ({ data: [] })),
              track.likes !== undefined && track.likes !== null
                ? Promise.resolve(Number(track.likes) || 0)
                : getTrack(axios, track.id)
                  .then(({ data }) => Number(data && data.favs) || 0)
                  .catch(() => 0),
            ]);
            const comments = Array.isArray(repliesResult.data) ? repliesResult.data : [];
            return { ...track, comments, likes };
          })
        );
        if (!cancelled) {
          setTracks(withComments);
          setDates(weekDates);
        }
      } catch (error) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const weekRange = formatWeekRange(dates);

  return (
    <div className="popular-last-week-page">
      <Helmet>
        <title>Popular Tracks from Last Week | Dream Chimney</title>
        <link rel="canonical" href="https://www.dreamchimney.com/tracks/popular/last-week" />
      </Helmet>
      <Row>
        <Col xs={12}>
          <h1 className="page-header">
            <FontAwesomeIcon icon="thumbs-up" className="thumbs-up" />
            Popular Tracks from Last Week
          </h1>
          <p className="popular-week-intro">
            {weekRange ? `The tracks with most comments and likes during ${weekRange}.` : "The tracks that received the most comments and likes last week."}{" "}

          </p>
        </Col>
      </Row>

      {loading && <Skeleton count={6} />}
      {!loading && failed && <p>Couldn&apos;t load last week&apos;s popular tracks.</p>}
      {!loading && !failed && tracks.length === 0 && <p>No popular tracks from last week yet.</p>}

      {!loading && !failed && tracks.length > 0 && <TrackOverview tracks={tracks} />}

      {!loading &&
        !failed &&
        tracks.map((track) => (
          <PopularTrack track={track} key={track.id} />
        ))}

      {!loading && !failed && tracks.length > 0 && <ShareSummary tracks={tracks} weekRange={weekRange} />}

      <div className="popular-week-back">
        <Button variant="outline-secondary" as={Link} to="/">
          Back to today&apos;s tracks
        </Button>
      </div>
    </div>
  );
};

export default PopularLastWeekPage;
